/** GHA price-validation bot diagnostics — gated on verifier user agent throughout. */

export const GHA_STEPS = [
  { id: 'geo', label: 'Geo region' },
  { id: 'offer', label: 'Offer' },
  { id: 'mapping', label: 'Property mapping' },
  { id: 'content', label: 'Property content' },
  { id: 'rates', label: 'Rates' },
]

export const GHA_CLOCK_CLASS = 'gha-diag-clock'

/**
 * Digit reels for the elapsed clock, most significant first.
 *
 * A JS clock is useless here: the rates call jams the main thread for seconds at
 * a time, so neither the interval nor the DOM write can run. Each reel is instead
 * a strip of digits scrolled by a `steps()` transform animation, which Chrome runs
 * on the compositor — the same reason the page's loading spinner keeps turning
 * through the jam. Reels start when the SSR markup is parsed, so the clock is
 * live before any bundle loads.
 */
export const GHA_CLOCK_REELS = [
  { name: 'min', digits: 10, durationSec: 600, separator: ':' },
  { name: 'ten', digits: 6, durationSec: 60 },
  { name: 'sec', digits: 10, durationSec: 10, separator: '.' },
  { name: 'tenth', digits: 10, durationSec: 1 },
]

/**
 * One `translateY(-100%)` per cycle moves the strip by its own full height, so
 * `steps(digits)` advances exactly one digit per step.
 */
export const GHA_CLOCK_CSS = [
  `.${GHA_CLOCK_CLASS}-reel{display:inline-block;height:1.6em;line-height:1.6em;overflow:hidden;vertical-align:top}`,
  `.${GHA_CLOCK_CLASS}-reel>span{display:block;will-change:transform;animation-name:${GHA_CLOCK_CLASS}-roll;animation-iteration-count:infinite}`,
  `@keyframes ${GHA_CLOCK_CLASS}-roll{from{transform:translateY(0)}to{transform:translateY(-100%)}}`,
  `.${GHA_CLOCK_CLASS}-frozen .${GHA_CLOCK_CLASS}-reel>span{animation-play-state:paused}`,
  ...GHA_CLOCK_REELS.map(reel =>
    `.${GHA_CLOCK_CLASS}-${reel.name}>span{animation-duration:${reel.durationSec}s;animation-timing-function:steps(${reel.digits})}`,
  ),
].join('')

/** Verdict on what SSR delivered before the data-fetching timeout. */
export function describeSsrDelivery(ssrMask: string | null): [verdict: string, detail?: string] {
  if (ssrMask === null) return ['n/a - client-side nav', 'nothing was server rendered']
  if (!ssrMask) return ['n/a - not a partner page']
  if (ssrMask[0] !== '1') return ['FAILED - sent no geo', 'timed out, or fetch failed']

  const missing = GHA_STEPS
    .filter((_, index) => ssrMask[index] !== '1')
    .map(step => step.label.toLowerCase())

  if (missing.length) {
    return [
      'PARTIAL - incomplete SSR',
      `missing ${missing.join(', ')}`,
    ]
  }

  return ['ok - server prefetches complete']
}

const GHA_VERIFIER_UA = 'Google-HotelAdsVerifier'

/** True on `/partner/` pages loaded by the GHA price-validation bot. */
export function isGhaVerifier(state: App.State): boolean {
  // Prefer server UA so SSR and client agree; navigator is a fallback.
  const userAgent = state.system.rawUserAgentString ||
    (typeof navigator === 'undefined' ? '' : navigator.userAgent)

  return state.router.location.pathname.includes('/partner/') &&
    userAgent.includes(GHA_VERIFIER_UA)
}

/** `/au/partner/:slug/:offerId` → offerId */
export function getPartnerOfferId(pathname: string): string {
  return pathname.match(/\/partner\/[^/]+\/([^/?#]+)/)?.[1] ?? ''
}

interface PartnerPageData {
  offerId: string
  offer: App.BedbankOffer | undefined
  mappingRecord: App.OfferState['propertyOfferMappings'][string] | undefined
  mapping: App.PropertyOfferMapping | undefined
  property: App.OfferState['properties'][string] | undefined
  hasValidPropertyMapping: boolean
  hasReadyRates: boolean
}

function getPartnerPageData(state: App.State): PartnerPageData | undefined {
  const pathname = state.router.location.pathname
  if (!pathname.includes('/partner/')) return undefined

  const offerId = getPartnerOfferId(pathname)
  const offer = state.offer.bedbankOffers[offerId]
  const mappingRecord = state.offer.propertyOfferMappings[offerId]
  const mapping = mappingRecord?.mapping
  const propertyId = mapping?.propertyId
  const property = propertyId ? state.offer.properties[propertyId] : undefined
  const hasValidPropertyMapping = !!mapping?.bedbankPropertyIds?.includes(offerId)
  const rates = Object.values(state.offer.propertyRates)
  const hasReadyRates = rates.some(rate => !!rate?.rooms?.length && !rate.fetching)

  return {
    offerId,
    offer,
    mappingRecord,
    mapping,
    property,
    hasValidPropertyMapping,
    hasReadyRates,
  }
}

/** Step completion as a stable string like `'1100'` to avoid needless re-renders. */
export function getGhaStepsMask(state: App.State): string {
  const data = getPartnerPageData(state)
  if (!data) return ''

  return [
    state.geo.initialised,
    !!data.offer && !state.offer.offersLoading[data.offerId],
    !!data.mapping && !data.mappingRecord?.fetching,
    !!data.property?.property && !data.property.fetching,
    data.hasReadyRates,
  ].map(Number).join('')
}

/** The partner-page surface selected by the same conditions as BedbankOfferPage. */
export function describeGhaPageSurface(state: App.State): string {
  const data = getPartnerPageData(state)
  if (!data) return 'not a partner page'
  if (!data.mappingRecord || data.mappingRecord.fetching) return 'waiting for mapping'
  if (!data.hasValidPropertyMapping) return 'BedbankOfferContainer'
  if (!data.property?.property) return 'waiting for property content'
  return 'PropertyContainer'
}

/**
 * What is still holding a visible spinner, matching real UI gates — not just Redux prefetch.
 * Page chunk is separate: loadable(`BedbankOfferPage`) shows LoadingIndicator while the JS loads
 * even when every SSR prefetch checkbox is already green.
 */
export function describeGhaUiBlocker(
  state: App.State,
  options: { hydrated: boolean, pageChunkReady: boolean },
): string {
  if (!state.geo.initialised) return 'blocked: app geo spinner'
  if (!options.hydrated) return 'blocked: waiting for hydration'
  if (!options.pageChunkReady) return 'blocked: partner page JS chunk'

  const data = getPartnerPageData(state)
  if (!data) return 'blocked: not a partner page'

  const mappingFetchRequired = !!data.offerId && !data.mappingRecord
  const fetchingMapping = data.mappingRecord?.fetching ?? mappingFetchRequired
  if (fetchingMapping) return 'blocked: full-page spinner (mapping)'
  if (!data.offer || (state.offer.offersLoading[data.offerId] && !data.offer)) {
    return 'blocked: full-page spinner (offer)'
  }
  if (data.hasValidPropertyMapping && !data.property?.property) {
    return 'blocked: full-page spinner (property content)'
  }
  if (!data.hasReadyRates) return 'blocked: PropertySummary spinner (rates)'
  return 'ui: ready'
}

export function elapsedMs(): number {
  return typeof performance !== 'undefined' ? performance.now() : 0
}

/** TTFB (≈ SSR duration). Undefined during SSR itself. */
export function serverTimeMs(): number | undefined {
  if (typeof performance === 'undefined') return undefined

  const [navigation] = performance.getEntriesByType('navigation')

  return navigation?.responseStart
}

/** e.g. `0:03.45` */
export function formatElapsed(ms: number): string {
  const seconds = Math.max(0, ms) / 1000
  const [whole, fraction = '00'] = (seconds % 60).toFixed(2).split('.')

  return `${Math.floor(seconds / 60)}:${whole.padStart(2, '0')}.${fraction}`
}
